import { Injectable, OnDestroy, inject } from "@angular/core";
import moment from "moment";
import { ReplaySubject, Subject, combineLatest, of } from "rxjs";
import {
    filter,
    shareReplay,
    switchMap,
    takeUntil,
    withLatestFrom
} from "rxjs/operators";
import { Period } from "~app/common/models/period";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { ChangeLeveransplanPeriodCommand } from "../commands/Leveransplan/ChangeLeveransplanPeriodCommand";
import { CreateLeveransplanCommand } from "../commands/Leveransplan/CreateLeveransplanCommand";
import { RemoveLeveransplanCommand } from "../commands/Leveransplan/RemoveLeveransplanCommand";
import { UpdateLeveransplanCommand } from "../commands/Leveransplan/UpdateLeveransplanCommand";
import { Leveransplan, Leveransvirkessplan } from "../models/leveransvirkesplan-models";
import { AvtalService } from "./avtal.service";
import { KontraktCommandService } from "./kontrakt-command.service";
import { KontraktMediatorService } from "./kontrakt-mediator.service";

@Injectable()
export class LeveransvirkesplanMediatorService implements OnDestroy {
    private readonly avtalService = inject(AvtalService);
    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly kontraktCommandService = inject(KontraktCommandService);

    private readonly vkLeveransvirkesplanSrc = new ReplaySubject<Leveransvirkessplan>();
    get vkLeveransvirkesplan$() {
        return this.vkLeveransvirkesplanSrc.asObservable();
    }

    private readonly destroy$ = new Subject<void>();

    constructor() {

        this.kontraktMediatorService.kontrakt$.pipe(
            switchMap(kontrakt => {
                if(kontrakt.id > 0) {
                    return this.avtalService.getLeveransvirkesplanForKontrakt(kontrakt.id);
                }
                const leveransvirkesplan = new Leveransvirkessplan();
                leveransvirkesplan.kontraktGuid = kontrakt.guid;
                leveransvirkesplan.kontraktAffarsform = kontrakt.affarsform;
                leveransvirkesplan.kontraktGiltighetFrom = moment(kontrakt.kontraktGiltigtFrom).toDate();
                leveransvirkesplan.kontraktGiltighetTom = moment(kontrakt.kontraktGiltigtTom).toDate();
                leveransvirkesplan.anbudGiltighetFrom = moment(kontrakt.anbudGiltighetFrom).toDate();
                leveransvirkesplan.anbudGiltighetTom = moment(kontrakt.anbudGiltighetTom).toDate();

                return of(leveransvirkesplan);
            }),
            shareReplay(),
            takeUntil(this.destroy$)
        ).subscribe(leveransvirkesplan => {
            this.vkLeveransvirkesplanSrc.next(leveransvirkesplan);
        });

        this.kontraktMediatorService.anbudGiltigtChanged$.pipe(
            filter((period) => ObjectUtils.hasValue(period)),
            withLatestFrom(this.vkLeveransvirkesplan$),
            takeUntil(this.destroy$)
        ).subscribe(([
            period,
            leveransvirkesplan
        ]) => {
            leveransvirkesplan.anbudGiltighetFrom = period.periodFrom ?? null;
            leveransvirkesplan.anbudGiltighetTom = period.periodTom ?? null;
            this.vkLeveransvirkesplanSrc.next(leveransvirkesplan);

            if(leveransvirkesplan.isPeriodValid()) {
                this.changeLeveransvirkesplanPerioder(this.getStartOfMonth(period));
            }
        });

        this.kontraktMediatorService.kontraktGiltigtChanged$.pipe(
            filter((period) => ObjectUtils.hasValue(period)),
            withLatestFrom(this.vkLeveransvirkesplan$),
            takeUntil(this.destroy$)
        ).subscribe(([
            period,
            leveransvirkesplan
        ]) => {
            leveransvirkesplan.kontraktGiltighetFrom = period.periodFrom ?? null;
            leveransvirkesplan.kontraktGiltighetTom = period.periodTom ?? null;
            this.vkLeveransvirkesplanSrc.next(leveransvirkesplan);

            if(leveransvirkesplan.isPeriodValid()) {
                this.changeLeveransvirkesplanPerioder(this.getStartOfMonth(period));
            }
        });

        combineLatest([
            this.kontraktMediatorService.atgardForeslagetProduktionsdatumchanged$,
            this.vkLeveransvirkesplan$
        ]).pipe(
            takeUntil(this.destroy$)
        ).subscribe(([
            atgardPeriod,
            leveransvirkesplan
        ]) => {
            // Sätt modellen för att validera åtgärders datum och sätta status på fördela-knappen
            if(ObjectUtils.hasValue(atgardPeriod)) {
                const atgard = leveransvirkesplan.atgarderForeslagetProduktionsdatum.find(x => x.guid === atgardPeriod.guid);
                if (ObjectUtils.hasValue(atgard)) {
                    atgard.periodFrom = atgardPeriod.periodFrom;
                    atgard.periodTom = atgardPeriod.periodTom;
                }
            }
        });
    }
    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    getStartOfMonth(period: Period): Period {
        const periodToReturn = new Period();
        periodToReturn.periodFrom = moment(period.periodFrom).startOf("month").toDate();
        periodToReturn.periodTom = moment(period.periodTom).startOf("month").toDate();
        return periodToReturn;
    }

    createLeveransvirkesplan(leveransvirkesplan: Leveransplan) {
        const cmd = new CreateLeveransplanCommand(leveransvirkesplan);
        this.kontraktCommandService.update(cmd);
    }

    updateLeveransvirkesplan(leveransvirkesplan: Leveransplan) {
        const cmd = new UpdateLeveransplanCommand(leveransvirkesplan);
        this.kontraktCommandService.update(cmd);
    }

    removeLeveransvirkesplan(leveransvirkesplan: Leveransplan) {
        const cmd = new RemoveLeveransplanCommand(leveransvirkesplan);
        this.kontraktCommandService.update(cmd);
    }

    changeLeveransvirkesplanPerioder(period: Period) {
        const kontraktGuid = this.kontraktMediatorService.kontrakt.guid;
        const cmd = new ChangeLeveransplanPeriodCommand(kontraktGuid, period);
        this.kontraktCommandService.update(cmd);
    }
}
